// Собствениците на пропърти мениджъра: кой какви имоти има, телефон за връзка,
// закачане на свободен имот и премахване — всичко на едно място.

import React, { useCallback, useState } from 'react';
import { Linking, Pressable, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api } from '../../api';
import { showError, showSuccess } from '../../toast';
import { Badge, Button, Card, COLORS, EmptyState, Muted, Screen, Select, SP, Title } from '../../ui';
import { t } from '../../i18n';

type OwnerApartment = { id: string; address: string; city: string; status: string; managementPercent: number | null };
type Owner = {
  id: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string;
  registered: boolean;
  apartments: OwnerApartment[];
};

export default function OwnersScreen({ navigation }: any) {
  const [owners, setOwners] = useState<Owner[] | null>(null);
  const [unassigned, setUnassigned] = useState<Array<{ id: string; address: string; city: string }>>([]);
  const [busy, setBusy] = useState(false);
  // „Добави имот“ е отворено за този собственик (телефонът е ключът)
  const [addingFor, setAddingFor] = useState<string | null>(null);
  // Премахване с потвърждение на втория натиск — Alert.alert не работи в уеб
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const load = useCallback(() => {
    api('/apartments/owners')
      .then((r) => setOwners(r.owners))
      .catch((e) => showError(e.message));
    api('/apartments/owners/unassigned')
      .then((r) => setUnassigned(r.apartments))
      .catch(() => {});
  }, []);

  useFocusEffect(useCallback(() => load(), [load]));

  const attach = async (owner: Owner, label: string) => {
    const apartment = unassigned.find((a) => `${a.address}, ${a.city}` === label);
    if (!apartment) return;
    setBusy(true);
    try {
      await api(`/apartments/${apartment.id}/owner`, {
        body: {
          ownerPhone: owner.phone,
          ownerFirstName: owner.firstName ?? undefined,
          ownerLastName: owner.lastName ?? undefined,
        },
      });
      setAddingFor(null);
      showSuccess(t('Имотът е добавен към собственика.'));
      load();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (apartmentId: string) => {
    setBusy(true);
    try {
      await api(`/apartments/${apartmentId}/owner`, { method: 'DELETE' });
      setConfirmRemove(null);
      showSuccess(t('Имотът е премахнат от собственика.'));
      load();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (owners === null) {
    return (
      <Screen>
        <Title>{t('Собственици')}</Title>
        <Muted>{t('Зареждане…')}</Muted>
      </Screen>
    );
  }

  return (
    <Screen>
      <Title>{t('Собственици')}</Title>
      <Muted>
        {t('Всички собственици, чиито имоти управлявате — с телефон и имотите на всеки. Нов собственик се добавя при създаване на имот или от бутона „Добави имот“ по-долу.')}
      </Muted>

      {owners.length === 0 && (
        <EmptyState
          icon="👤"
          title={t('Още няма собственици')}
          text={t('Добавете имот и посочете собственика му по телефон — той ще получи SMS с покана.')}
        />
      )}

      {owners.map((o) => {
        const name = [o.firstName, o.lastName].filter(Boolean).join(' ') || t('Без име');
        return (
          <Card key={o.phone}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={{ fontSize: 17, fontWeight: '700', color: COLORS.text, flex: 1 }}>{name}</Text>
              <Badge
                text={o.registered ? t('регистриран') : t('поканен')}
                color={o.registered ? COLORS.success : COLORS.warn}
              />
            </View>
            <Pressable onPress={() => Linking.openURL(`tel:${o.phone}`)}>
              <Text style={{ color: COLORS.primary, marginTop: 2 }}>{o.phone}</Text>
            </Pressable>

            <View style={{ marginTop: SP.sm, gap: 6 }}>
              {o.apartments.map((a) => (
                <View
                  key={a.id}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: COLORS.bg,
                    borderRadius: 10,
                    paddingHorizontal: 12,
                    paddingVertical: 10,
                    gap: 8,
                  }}
                >
                  <Pressable style={{ flex: 1 }} onPress={() => navigation.navigate('ApartmentDetail', { id: a.id })}>
                    <Text style={{ color: COLORS.text }}>
                      {a.address}, {a.city}
                    </Text>
                    {a.managementPercent !== null && <Muted>{t('{n}% за управление', { n: a.managementPercent })}</Muted>}
                  </Pressable>
                  {confirmRemove === a.id ? (
                    <Pressable onPress={() => remove(a.id)} disabled={busy}>
                      <Text style={{ color: COLORS.danger, fontWeight: '700' }}>{t('Потвърди ✕')}</Text>
                    </Pressable>
                  ) : (
                    <Pressable onPress={() => setConfirmRemove(a.id)}>
                      <Text style={{ color: COLORS.muted }}>{t('Премахни')}</Text>
                    </Pressable>
                  )}
                </View>
              ))}
              {o.apartments.length === 0 && <Muted>{t('Няма имоти — поканата чака регистрация.')}</Muted>}
            </View>

            {addingFor === o.phone ? (
              unassigned.length > 0 ? (
                <Select
                  placeholder={t('Изберете свободен имот…')}
                  options={unassigned.map((a) => `${a.address}, ${a.city}`)}
                  value={null}
                  onChange={(label: string) => attach(o, label)}
                />
              ) : (
                <Muted>{t('Няма свободни имоти — всички вече имат собственик.')}</Muted>
              )
            ) : (
              <Button title={t('Добави имот')} variant="secondary" onPress={() => setAddingFor(o.phone)} />
            )}
          </Card>
        );
      })}
    </Screen>
  );
}
